export type TimezoneWriteTicket = Readonly<{ uid: string; revision: number }>;
type TimezoneResponseBody = {
  user?: { uid?: string; timezone?: string | null } | null;
};
type SetTimezoneState<T> = (updater: (current: T | null) => T | null) => void;

/** Keeps delayed timezone responses from repainting a newer identity or choice. */
export class TimezoneWriteGuard {
  private revision = 0;
  private activeUid: string | null = null;

  transition(uid: string | null): void {
    if (uid === this.activeUid) return;
    this.activeUid = uid;
    this.invalidate();
  }

  begin(uid: string): TimezoneWriteTicket {
    this.transition(uid);
    return { uid, revision: ++this.revision };
  }

  invalidate(): void {
    this.revision += 1;
  }

  isCurrent(ticket: TimezoneWriteTicket): boolean {
    return ticket.uid === this.activeUid && ticket.revision === this.revision;
  }

  applyResponse<T extends { uid: string; timezone?: string | null }>(
    ticket: TimezoneWriteTicket,
    current: T | null,
    timezone: string | null,
  ): T | null {
    if (
      !current ||
      current.uid !== ticket.uid ||
      ticket.revision !== this.revision
    ) {
      return current;
    }
    return { ...current, timezone };
  }
}

/** Apply a successful timezone response only while its write is still current. */
export async function applyTimezoneWrite<
  T extends { uid: string; timezone?: string | null },
  R extends TimezoneResponseBody,
>(
  guard: TimezoneWriteGuard,
  ticket: TimezoneWriteTicket,
  write: () => Promise<R>,
  setCurrent: SetTimezoneState<T>,
): Promise<R> {
  const response = await write();
  setCurrent((current) =>
    guard.applyResponse(ticket, current, response.user?.timezone ?? null),
  );
  return response;
}

/**
 * If an explicit write fails, reconcile a possibly committed earlier capture.
 * The original write error remains the one surfaced to the caller.
 */
export async function applyTimezoneWriteWithReconciliation<
  T extends { uid: string; timezone?: string | null },
  R extends TimezoneResponseBody,
>(
  guard: TimezoneWriteGuard,
  ticket: TimezoneWriteTicket,
  write: () => Promise<R>,
  reconcile: () => Promise<R>,
  pendingCapture: Promise<unknown> | null,
  setCurrent: SetTimezoneState<T>,
): Promise<R> {
  try {
    return await applyTimezoneWrite(guard, ticket, write, setCurrent);
  } catch (writeError) {
    void (async () => {
      try {
        if (pendingCapture) {
          try {
            await pendingCapture;
          } catch {
            // A lost capture response can follow a successful server commit.
          }
        }
        if (!guard.isCurrent(ticket)) return;
        const response = await reconcile();
        if (response.user?.uid === ticket.uid) {
          setCurrent((current) =>
            guard.applyResponse(ticket, current, response.user?.timezone ?? null),
          );
        }
      } catch {
        // Keep the explicit save error visible; reconciliation is best-effort.
      }
    })();
    throw writeError;
  }
}
